import { useRef, useState, useEffect } from "react";
import HelpingBubble from "../common/Help"; 

export default function SeedInput({ value, onChange, pasteMode }) {
    const defaultMsg = () => (pasteMode ? "Paste a valid seed here!" : "Click to copy!");

    const SEED_LENGTH = 30;
    const inputsRef = useRef([]);
    const [isHovering, setIsHovering] = useState(false);
    const [msg, setMsg] = useState(defaultMsg());

    // Ensure value has exactly SEED_LENGTH digits
    const seedArray = value.toString().padStart(SEED_LENGTH, "0").slice(-SEED_LENGTH).split("");

    useEffect(() => {
        if (!pasteMode) return; // Only handle pasting if pasteMode is enabled

        const handlePaste = (e) => {
            if (!isHovering) return; // Only allow pasting if hovering

            e.preventDefault();
            const pasteData = e.clipboardData.getData("text");

            // Validate: Must be exactly SEED_LENGTH digits, no non-numeric characters
            if (/^\d{30}$/.test(pasteData)) {
                onChange(pasteData); // Update parent state only if valid
            }
        };

        if (isHovering) {
            document.addEventListener("paste", handlePaste);
        } else {
            document.removeEventListener("paste", handlePaste);
        }

        return () => document.removeEventListener("paste", handlePaste);
    }, [isHovering, onChange, pasteMode]);

    const handleCopy = () => {
        if (!pasteMode) {
            navigator.clipboard.writeText(seedArray.join("")).then(() => {
                setMsg("Copied!");
            });
        }
    };

    const handleMouseLeave = () => {
        setIsHovering(false);
        setMsg(defaultMsg());
    };

    return (
        <HelpingBubble description={msg}>
            <div
                className={`text-[8px] lg:text-base flex justify-center mx-auto gap-0.5 p-1 lg:p-2 rounded-xl bg-gray-900/50 transition-all focus:outline-none select-none cursor-pointer
                ${pasteMode ? "hover:bg-gray-700/50" : "hover:bg-gray-900/80 cursor-pointer"}`}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={handleMouseLeave}
                onClick={handleCopy} // Copy seed when clicking in copy mode
            >
                {seedArray.map((digit, index) => (
                    <input
                        key={index}
                        ref={(el) => (inputsRef.current[index] = el)}
                        type="text"
                        value={digit}
                        maxLength={1}
                        className="w-2 h-3 lg:w-4 lg:h-6 text-center rounded-md font-mono font-bold text-gray-300 bg-gray-800 border border-gray-500 shadow-md
                        focus:outline-none select-none cursor-pointer transition-all 
                        hover:bg-gray-700 hover:text-white"
                        inputMode="none"
                        readOnly
                        tabIndex="-1" // Prevent keyboard focus
                        style={{
                            caretColor: "transparent",
                            textShadow: "0 0 6px #ccc",
                        }}
                    />
                ))}
            </div>
        </HelpingBubble>
    );
}
